/**
 * Lazy Optimizely Forms Utility
 *
 * When "Enable Lazy Load Forms" is on in Site Settings, the server renders the Optimizely Forms
 * runtime (Forms jQuery, prerequisite script, EPiServerForms.js) with type="text/lazy-epiforms"
 * (see LazyFormScriptsHelper.cs), so the browser neither downloads nor executes them.
 * EPiServerForms.js is what pulls in reCAPTCHA and the custom element scripts, so those wait too.
 *
 * This util re-activates the scripts, in document order, when whichever comes first:
 * - a form is already visible once the page has loaded → straight away
 * - the user focuses / presses inside a form → straight away
 * - the user's first activity anywhere (scroll, mouse move, touch, key) → when the browser is idle,
 *   so the heavy reCAPTCHA parse doesn't land on top of that first interaction
 *
 * A native submit that happens while the runtime is still loading is held back (so it can't post
 * without client validation / reCAPTCHA) and replayed once Forms is ready. If the runtime fails to
 * load or never initialises (e.g. reCAPTCHA blocked), the hold is released and the replayed submit
 * posts natively, falling back to Forms' server-side (non-JS) handling as without lazy loading.
 */
import { devLogger } from "@/utils/dev-logger.util";
import { loadMethodOnUserAction } from "@/utils/user-interaction.util";
const LAZY_SCRIPT_SELECTOR = 'script[type="text/lazy-epiforms"]';
const FORM_SELECTOR = '.EPiServerForms, [data-f-type="form"]';
const FORM_INTERACTION_EVENTS = ["focusin", "pointerdown"];
const IDLE_TIMEOUT_MS = 2000;
// How long after our scripts load we wait for Forms' own external scripts (reCAPTCHA) and init()
const INIT_TIMEOUT_MS = 8000;
const READY_POLL_MS = 100;
// Forms binds its handlers in a jQuery ready callback, which runs just after init() flips the flag
const REPLAY_DELAY_MS = 50;
let loadPromise = null;
let loadState = "idle";
let loadedAt = 0;
function getEpiForms() {
    return window.epi?.EPiServer?.Forms;
}
function isFormsInitialized() {
    return getEpiForms()?.__Initialized === true;
}
/**
 * True while a held submit should keep waiting for Forms: the runtime is not initialised yet and
 * is still loading, or loaded recently enough that init() may still be pending.
 */
function isWaitingForForms() {
    if (isFormsInitialized()) {
        return false;
    }
    switch (loadState) {
        case "idle":
        case "loading":
            return true;
        case "loaded":
            return Date.now() - loadedAt < INIT_TIMEOUT_MS;
        default:
            return false;
    }
}
function isInsideForm(target) {
    return target instanceof Element && target.closest(FORM_SELECTOR) !== null;
}
/**
 * Forms focuses the first field of a "Focus on form" form during init(). That's expected at page
 * load, but when init() runs later (after the user's first scroll / mouse move, or once they are
 * already in the form) it would yank the viewport or the caret, so switch it off.
 */
function suppressFocusOnForm() {
    const epiForms = getEpiForms();
    if (!epiForms) {
        return;
    }
    Object.values(epiForms).forEach((value) => {
        if (value && typeof value === "object" && value.FocusOnForm === true) {
            value.FocusOnForm = false;
        }
    });
}
/**
 * Swap an inert script for an executable copy. External scripts resolve on load so the
 * next one in the chain only runs once its dependency (e.g. jQuery) is available.
 */
function activateScript(inert) {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        Array.from(inert.attributes).forEach((attr) => {
            if (attr.name !== "type" && attr.name !== "nonce") {
                script.setAttribute(attr.name, attr.value);
            }
        });
        if (inert.nonce) {
            script.nonce = inert.nonce;
        }
        if (inert.src) {
            script.async = false;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load ${inert.src}`));
            inert.replaceWith(script);
            return;
        }
        // Inline scripts execute synchronously on insertion
        script.text = inert.text;
        inert.replaceWith(script);
        resolve();
    });
}
function loadFormScripts(scripts) {
    if (!loadPromise) {
        loadState = "loading";
        loadPromise = scripts
            .reduce((chain, script) => chain.then(() => activateScript(script)), Promise.resolve())
            .then(() => {
            loadState = "loaded";
            loadedAt = Date.now();
        })
            .catch((error) => {
            loadState = "failed";
            devLogger.error("Failed to load Optimizely Forms scripts:", error);
        });
    }
    return loadPromise;
}
/**
 * Re-run a held submit once Forms is ready (its click handler takes over), or once we've given up
 * waiting (the submit is then no longer intercepted and posts natively).
 */
function replayWhenReady(form, submitter) {
    const timer = window.setInterval(() => {
        if (isWaitingForForms()) {
            return;
        }
        window.clearInterval(timer);
        window.setTimeout(() => {
            if (submitter && form.contains(submitter)) {
                submitter.click();
            }
            else if (typeof form.requestSubmit === "function") {
                form.requestSubmit();
            }
            else {
                form.submit();
            }
        }, REPLAY_DELAY_MS);
    }, READY_POLL_MS);
}
function whenPageLoaded(callback) {
    if (document.readyState === "complete") {
        callback();
        return;
    }
    window.addEventListener("load", callback, { once: true });
}
function whenIdle(callback) {
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
        return;
    }
    setTimeout(callback, 1);
}
export function initLazyForms() {
    const scripts = Array.from(document.querySelectorAll(LAZY_SCRIPT_SELECTOR));
    if (scripts.length === 0) {
        return;
    }
    const forms = Array.from(document.querySelectorAll(FORM_SELECTOR));
    let pendingSubmit = false;
    const load = (trigger) => {
        FORM_INTERACTION_EVENTS.forEach((eventName) => {
            document.removeEventListener(eventName, onFormInteraction, true);
        });
        if (loadPromise) {
            return;
        }
        if (trigger !== "visible") {
            suppressFocusOnForm();
        }
        void loadFormScripts(scripts);
    };
    const onFormInteraction = (event) => {
        if (isInsideForm(event.target)) {
            load("interaction");
        }
    };
    const onSubmit = (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !isInsideForm(form) || !isWaitingForForms()) {
            return;
        }
        event.preventDefault();
        load("interaction");
        // Several clicks while loading still replay just one submit
        if (!pendingSubmit) {
            pendingSubmit = true;
            const submitter = event.submitter ?? null;
            replayWhenReady(form, submitter);
        }
    };
    // Nothing to watch, or no observer support: fall back to loading straight away
    if (forms.length === 0 || typeof IntersectionObserver === "undefined") {
        load("visible");
        return;
    }
    document.addEventListener("submit", onSubmit, true);
    FORM_INTERACTION_EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, onFormInteraction, { capture: true, passive: true });
    });
    // Any first activity on the page; load() is a no-op once the scripts are loading
    loadMethodOnUserAction([() => whenIdle(() => load("activity"))]);
    // Checked after load so a form above the fold doesn't compete with LCP; a form that is
    // already on screen must work without waiting for the user to scroll or move the mouse
    whenPageLoaded(() => {
        if (loadPromise) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            observer.disconnect();
            if (entries.some((entry) => entry.isIntersecting)) {
                load("visible");
            }
        });
        forms.forEach((form) => observer.observe(form));
    });
}
